import { useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Avatar } from "../components/ui";
import { shortDate, relDate } from "../lib/format";
import { roleLabel } from "../lib/automation";
import type { Task, TaskStatus, TaskPriority, Assignee, TaskType, TaskField, TaskFieldValue } from "../lib/types";
import { missingRequiredFields } from "../lib/taskTypes";
import * as work from "../lib/workClient";

const COLUMNS: { key: TaskStatus; label: string }[] = [
  { key: "todo", label: "To do" },
  { key: "doing", label: "In progress" },
  { key: "done", label: "Done" },
];
const PRIORITY_COLOR: Record<TaskPriority, string> = { high: "var(--risk)", medium: "var(--watch)", low: "var(--good)" };
const SOURCE_LABEL: Record<Task["source"], string> = { manual: "Manual", playbook: "Playbook", automation: "Automation" };
const nextStatus: Record<TaskStatus, TaskStatus | null> = { todo: "doing", doing: "done", done: null };
const prevStatus: Record<TaskStatus, TaskStatus | null> = { todo: null, doing: "todo", done: "doing" };

/** Encode/parse an assignee as a select value, e.g. "role:csm" / "user:u2". */
const encodeAssignee = (a: Assignee): string => (a.kind === "role" ? `role:${a.role}` : `user:${a.userId}`);
const parseAssignee = (v: string): Assignee => {
  const [kind, id] = v.split(":");
  return kind === "role" ? { kind: "role", role: id } : { kind: "user", userId: id };
};

export default function Tasks() {
  const { ws, me, userById } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [tasks, setTasks] = useState<Task[]>(ws.tasks);
  const [types, setTypes] = useState<TaskType[]>(ws.taskTypes);
  const [assignedTo, setAssignedTo] = useState<string>("all");
  const [source, setSource] = useState<string>("all");
  const [composing, setComposing] = useState(false);
  const [completing, setCompleting] = useState<Task | null>(null);

  useEffect(() => { work.loadTasks(ws).then((r) => { setMode(r.mode); setTasks(r.tasks); }); work.loadTaskTypes(ws).then((r) => setTypes(r.types)); }, [ws]);
  const typeOf = (t: Task) => types.find((x) => x.id === t.typeId);

  const filtered = useMemo(() => tasks.filter((t) =>
    (assignedTo === "all" || t.ownerId === assignedTo) &&
    (source === "all" || t.source === source)
  ), [tasks, assignedTo, source]);

  const openCount = tasks.filter((t) => t.status !== "done").length;
  const overdue = tasks.filter((t) => t.status !== "done" && Date.parse(t.due) < Date.parse("2026-08-17")).length;
  const fromAutomation = tasks.filter((t) => t.source === "automation").length;

  const move = async (t: Task, status: TaskStatus) => {
    setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, status } : x)));
    // pass the acting user so completing a task stamps + reassigns to them
    const updated = await work.updateTask(mode, ws, t.id, { status }, me.id);
    if (updated) setTasks((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
  };
  // advancing to "done" runs the completion gate: a typed task with unmet
  // required fields opens the completion dialog instead of closing silently.
  const advance = async (t: Task, status: TaskStatus) => {
    if (status !== "done") return move(t, status);
    const type = typeOf(t);
    if (type && missingRequiredFields(type, t.fields).length > 0) { setCompleting(t); return; }
    return move(t, "done");
  };
  const finishComplete = async (t: Task, fields: Record<string, TaskFieldValue>) => {
    const res = await work.completeTask(mode, ws, t, fields, me.id);
    if (res.ok) {
      setTasks((prev) => prev.map((x) => (x.id === t.id ? res.task : x)));
      setCompleting(null);
    }
    return res;
  };
  const reassign = async (t: Task, assignee: Assignee) => {
    const updated = await work.updateTask(mode, ws, t.id, { assignee });
    setTasks((prev) => prev.map((x) => (x.id === t.id ? (updated ?? { ...x, assignee }) : x)));
  };
  const remove = async (t: Task) => {
    setTasks((prev) => prev.filter((x) => x.id !== t.id));
    await work.deleteTask(mode, ws, t.id);
  };
  const add = async (input: Parameters<typeof work.createTask>[2]) => {
    const created = await work.createTask(mode, ws, input);
    setTasks((prev) => [...prev, created]);
    setComposing(false);
  };

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Tasks</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>The work, assigned</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>
            Every task is owned — by a person, or by a role that resolves through the account's team.
            Reassign a role once and the work follows whoever holds it.
          </p>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <ModePill mode={mode} />
          <button className="btn btn--primary btn--sm" onClick={() => setComposing((v) => !v)}>
            <Icon name="plus" size={15} /> New task
          </button>
        </div>
      </div>

      <div className="stats">
        <Stat icon="check" label="Open tasks" value={String(openCount)} foot="Not yet done" />
        <Stat icon="clock" label="Overdue" value={String(overdue)} foot="Past their due date" color={overdue ? "var(--risk)" : undefined} />
        <Stat icon="bolt" label="From automations" value={String(fromAutomation)} foot="Created by a rule" />
        <Stat icon="user" label="Teammates" value={String(ws.users.length)} foot="Carrying assignments" />
      </div>

      <div className="toolbar" style={{ justifyContent: "space-between" }}>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <FilterSelect label="Assignee" value={assignedTo} onChange={setAssignedTo}
            options={[{ v: "all", l: "Everyone" }, ...ws.users.map((u) => ({ v: u.id, l: u.name }))]} />
          <FilterSelect label="Source" value={source} onChange={setSource}
            options={[{ v: "all", l: "All sources" }, { v: "manual", l: "Manual" }, { v: "playbook", l: "Playbook" }, { v: "automation", l: "Automation" }]} />
        </div>
        <span className="faint" style={{ fontSize: 12.5 }}>{filtered.length} shown</span>
      </div>

      {composing && (
        <Composer ws={ws} types={types} onCancel={() => setComposing(false)} onCreate={add} />
      )}

      <div className="kanban">
        {COLUMNS.map((col) => {
          const items = filtered.filter((t) => t.status === col.key);
          return (
            <div key={col.key} className="kanban__col">
              <div className="kanban__hd">
                <span>{col.label}</span>
                <span className="count">{items.length}</span>
              </div>
              <div className="kanban__body">
                {items.length === 0 ? (
                  <div className="kanban__empty">Nothing here</div>
                ) : items.map((t) => (
                  <TaskCard key={t.id} task={t} type={typeOf(t)} roleLabelOf={(k) => roleLabel(ws.roles, k)}
                    ownerName={userById(t.ownerId)?.name ?? "Unassigned"}
                    completedByName={t.completedBy ? (userById(t.completedBy)?.name ?? "Someone") : undefined}
                    assigneeOptions={ws}
                    onMoveNext={nextStatus[t.status] ? () => advance(t, nextStatus[t.status]!) : undefined}
                    onMovePrev={prevStatus[t.status] ? () => move(t, prevStatus[t.status]!) : undefined}
                    onReassign={(a) => reassign(t, a)}
                    onDelete={() => remove(t)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {completing && <CompletionDialog task={completing} type={typeOf(completing)!} onCancel={() => setCompleting(null)} onComplete={finishComplete} />}
    </div>
  );
}

function TaskCard({ task, type, ownerName, completedByName, roleLabelOf, assigneeOptions, onMoveNext, onMovePrev, onReassign, onDelete }: {
  task: Task; type?: TaskType; ownerName: string; completedByName?: string; roleLabelOf: (k: string) => string;
  assigneeOptions: ReturnType<typeof useWorkspace>["ws"];
  onMoveNext?: () => void; onMovePrev?: () => void; onReassign: (a: Assignee) => void; onDelete: () => void;
}) {
  const isRole = task.assignee.kind === "role";
  const overdue = task.status !== "done" && Date.parse(task.due) < Date.parse("2026-08-17");
  const missing = type ? missingRequiredFields(type, task.fields).length : 0;
  const gated = task.status !== "done" && missing > 0;
  return (
    <div className="task-card">
      <div className="row between" style={{ alignItems: "flex-start", gap: 8 }}>
        <span className="task-card__pri" style={{ background: PRIORITY_COLOR[task.priority] }} title={`${task.priority} priority`} />
        <b style={{ fontSize: 13.5, lineHeight: 1.4, flex: 1 }}>{task.title}</b>
        <button className="icon-btn" title="Delete task" onClick={onDelete}><Icon name="dot" size={14} /></button>
      </div>
      <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>{task.accountName}</div>

      <div className="row between" style={{ marginTop: 12 }}>
        <label className="assignee" title="Assignee">
          <Avatar name={ownerName} size="sm" />
          <select className="assignee__sel" value={encodeAssignee(task.assignee)} onChange={(e) => onReassign(parseAssignee(e.target.value))}>
            <optgroup label="Roles">
              {assigneeOptions.roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.label}</option>)}
            </optgroup>
            <optgroup label="People">
              {assigneeOptions.users.map((u) => <option key={u.id} value={`user:${u.id}`}>{u.name}</option>)}
            </optgroup>
          </select>
        </label>
        <span className={`task-due ${overdue ? "task-due--over" : ""}`}>{relDate(task.due)}</span>
      </div>

      <div className="row between" style={{ marginTop: 10 }}>
        <span className="row" style={{ gap: 6, flexWrap: "wrap" }}>
          {type && <span className="tag" style={{ color: "var(--corona-ink)", borderColor: "rgba(201,130,26,.3)" }} title="Task type — required fields gate completion"><Icon name={type.icon || "check"} size={11} /> {type.name}</span>}
          {isRole && <span className="tag tag--role" title="Role-based — follows whoever holds this role">{roleLabelOf((task.assignee as { role: string }).role)}</span>}
          {task.source !== "manual" && <span className="tag">{SOURCE_LABEL[task.source]}</span>}
        </span>
        <span className="row" style={{ gap: 4 }}>
          {onMovePrev && <button className="mini-btn" onClick={onMovePrev} title="Move back"><Icon name="arrowRight" size={13} className="flip" /></button>}
          {onMoveNext && <button className="mini-btn" onClick={onMoveNext} title={gated ? `${missing} required field${missing > 1 ? "s" : ""} to complete` : "Advance"}><Icon name={gated ? "lock" : "arrowRight"} size={13} /></button>}
        </span>
      </div>
      {gated && <div style={{ fontSize: 11, marginTop: 8, color: "var(--watch)", display: "flex", alignItems: "center", gap: 5 }}><Icon name="lock" size={11} /> {missing} required field{missing > 1 ? "s" : ""} before this can be completed</div>}
      {task.status === "done" && completedByName && (
        <div style={{ fontSize: 11, marginTop: 8, color: "var(--good)", display: "flex", alignItems: "center", gap: 5 }}>
          <Icon name="check" size={11} /> Completed by {completedByName}
          {task.completedBy && task.completedBy !== task.ownerId && <span className="faint">· assigned to {ownerName}</span>}
        </div>
      )}
      <div className="faint" style={{ fontSize: 11, marginTop: 8 }}>Due {shortDate(task.due)}</div>
    </div>
  );
}

function Composer({ ws, types, onCancel, onCreate }: {
  ws: ReturnType<typeof useWorkspace>["ws"];
  types: TaskType[];
  onCancel: () => void;
  onCreate: (input: Parameters<typeof work.createTask>[2]) => void;
}) {
  const [title, setTitle] = useState("");
  const [accountId, setAccountId] = useState(ws.accounts[0]?.id ?? "");
  const [assignee, setAssignee] = useState("role:csm");
  const [priority, setPriority] = useState<TaskPriority>("medium");
  const [due, setDue] = useState("2026-08-24");
  const [typeId, setTypeId] = useState<string>("");
  const chosen = types.find((t) => t.id === typeId);
  const valid = title.trim() && accountId;

  return (
    <div className="card composer">
      <div className="card__bd">
        <div className="composer__row">
          <input className="inp inp--title" placeholder="What needs to happen?" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </div>
        <div className="composer__grid">
          <Labeled label="Account">
            <select className="inp" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {ws.accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Labeled>
          <Labeled label="Assign to">
            <select className="inp" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <optgroup label="Roles">{ws.roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.label}</option>)}</optgroup>
              <optgroup label="People">{ws.users.map((u) => <option key={u.id} value={`user:${u.id}`}>{u.name}</option>)}</optgroup>
            </select>
          </Labeled>
          <Labeled label="Priority">
            <select className="inp" value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
              <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </Labeled>
          <Labeled label="Due">
            <input className="inp" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </Labeled>
          <Labeled label="Task type">
            <select className="inp" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
              <option value="">None</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Labeled>
        </div>
        {chosen && chosen.fields.some((f) => f.required) && (
          <div className="faint" style={{ fontSize: 12, marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <Icon name="lock" size={12} /> Requires before completion: {chosen.fields.filter((f) => f.required).map((f) => f.label).join(", ")}
          </div>
        )}
        <div className="row" style={{ gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button className="btn btn--sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn--primary btn--sm" disabled={!valid}
            onClick={() => onCreate({ title: title.trim(), accountId, assignee: parseAssignee(assignee), priority, due, source: "manual", status: "todo", typeId: typeId || undefined })}>
            Create task
          </button>
        </div>
      </div>
    </div>
  );
}

function CompletionDialog({ task, type, onCancel, onComplete }: {
  task: Task; type: TaskType; onCancel: () => void;
  onComplete: (t: Task, fields: Record<string, TaskFieldValue>) => Promise<{ ok: boolean }>;
}) {
  const [values, setValues] = useState<Record<string, TaskFieldValue>>({ ...task.fields });
  const [busy, setBusy] = useState(false);
  const missing = missingRequiredFields(type, values);
  const canComplete = missing.length === 0;
  const set = (key: string, v: TaskFieldValue) => setValues((s) => ({ ...s, [key]: v }));

  return (
    <div className="cmdk-overlay" onClick={onCancel}>
      <div className="compose-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="compose-modal__hd">
          <div className="row" style={{ gap: 8 }}><Icon name={type.icon || "check"} size={16} /><b>Complete: {type.name}</b></div>
          <button className="icon-btn" onClick={onCancel}><Icon name="dot" size={16} /></button>
        </div>
        <div className="compose-modal__bd" style={{ display: "grid", gap: 14, maxHeight: "62vh", overflowY: "auto" }}>
          <div className="faint" style={{ fontSize: 13 }}>{task.title} · {task.accountName}</div>
          {type.description && <div className="faint" style={{ fontSize: 12.5, marginTop: -6 }}>{type.description}</div>}
          {type.fields.map((f) => <FieldInput key={f.key} field={f} value={values[f.key]} onChange={(v) => set(f.key, v)} accounts={undefined} />)}
        </div>
        <div className="compose-modal__hd" style={{ borderTop: "1px solid var(--line-soft)", borderBottom: "none", justifyContent: "space-between" }}>
          <span className="faint" style={{ fontSize: 12 }}>{canComplete ? "All required fields filled" : `${missing.length} required field${missing.length > 1 ? "s" : ""} left`}</span>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn--sm" onClick={onCancel}>Cancel</button>
            <button className="btn btn--primary btn--sm" disabled={!canComplete || busy}
              onClick={async () => { setBusy(true); await onComplete(task, values); setBusy(false); }}>
              <Icon name="check" size={14} /> Mark complete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange, accounts }: {
  field: TaskField; value: TaskFieldValue | undefined; onChange: (v: TaskFieldValue) => void;
  accounts?: { id: string; name: string }[];
}) {
  const req = field.required ? <span style={{ color: "var(--risk)" }}> *</span> : null;
  if (field.kind === "checkbox") {
    return (
      <label className="row" style={{ gap: 9, fontSize: 14, cursor: "pointer" }}>
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        <span>{field.label}{req}</span>
      </label>
    );
  }
  return (
    <label style={{ display: "grid", gap: 5 }}>
      <span className="labeled__l">{field.label}{req}{field.help && <span className="faint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}> — {field.help}</span>}</span>
      {field.kind === "select" ? (
        <select className="inp" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select…</option>
          {(field.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : field.kind === "account" && accounts ? (
        <select className="inp" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select account…</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      ) : (
        <input className="inp" type={field.kind === "date" ? "date" : field.kind === "number" ? "number" : "text"}
          value={value === undefined ? "" : String(value)}
          onChange={(e) => onChange(field.kind === "number" ? Number(e.target.value) : e.target.value)} />
      )}
    </label>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="labeled"><span className="labeled__l">{label}</span>{children}</label>;
}
function FilterSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { v: string; l: string }[] }) {
  return (
    <label className="row" style={{ gap: 6, fontSize: 12.5 }}>
      <span className="faint">{label}</span>
      <select className="inp inp--sm" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
function Stat({ icon, label, value, foot, color }: { icon: string; label: string; value: string; foot: string; color?: string }) {
  return (
    <div className="stat">
      <div className="stat__label"><Icon name={icon} size={14} /> {label}</div>
      <div className="stat__value" style={{ color }}>{value}</div>
      <div className="stat__foot">{foot}</div>
    </div>
  );
}
function ModePill({ mode }: { mode: work.Mode }) {
  return <span className={`mode-pill ${mode === "api" ? "live" : ""}`} title={mode === "api" ? "Connected to the Penumbrai API" : "Standalone demo — changes saved to this browser"}>
    <i />{mode === "api" ? "Live API" : "Local demo"}
  </span>;
}
